import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type DepartmentItem = { id: string; name: string };

export function ChecklistsFilter() {
  const nav = useNavigate({ from: "/app/checklists" });
  const search = useSearch({ from: "/app/checklists" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const titleFilter = findCondition(filters, "title");
  const typeFilter = findCondition(filters, "type");
  const departmentFilter = findCondition(filters, "departmentId");
  const statusFilter = findCondition(filters, "status");

  const [searchValue, setSearchValue] = useState<string>(
    (titleFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((titleFilter?.value as string) || ""),
    [titleFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "title",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "title");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const departmentValue = (departmentFilter?.value as string) || "";
  const statusValue = (statusFilter?.value as string) || "";

  const hasFilter =
    !!titleFilter?.value ||
    !!typeFilter?.value ||
    !!departmentValue ||
    !!statusValue;

  return (
    <div
      className="mb-4 flex flex-col gap-2 px-2"
      data-tourid="checklist-filter"
    >
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາ Checklist (ຊື່ / ປະເພດ / ພະແນກ)..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <InfiniteCombobox<DepartmentItem>
            queryKey={["checklists-departments", "filter"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/checklists/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/checklists/departments/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={departmentValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "departmentId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "departmentId");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ເລືອກພະແນກ"
            className="h-8 w-full sm:w-40"
            clearable
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "active", label: "ໃຊ້ງານ" },
              { value: "draft", label: "ປັບປຸງ" },
              { value: "inactive", label: "ເລີກໃຊ້" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
